import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import { Link } from "modules/editor/blocks/Link";
import { Text } from "modules/editor/blocks/Text";
import type { BlockquoteProps } from "modules/editor/blocks/Blockquote";
import type { BodyProps } from "modules/editor/blocks/Body";
import type { CallToActionProps } from "modules/editor/blocks/CallToAction";
import type { EventsProps } from "modules/editor/blocks/Events";
import type { HeadingProps } from "modules/editor/blocks/Heading";
import type { IframeProps } from "modules/editor/blocks/Iframe";
import type { ImageProps } from "modules/editor/blocks/Image";
import type { ListProps } from "modules/editor/blocks/List";
import type { ParagraphProps } from "modules/editor/blocks/Paragraph";
import type { VideoProps } from "modules/editor/blocks/Video";
import { FrontendBlock, isBlockquoteBlock, isBodyBlock, isCallToActionBlock, isContentSummaryBlock, isCustomUrlBlock, isEventsBlock, isGridBlock, isHeadingBlock, isIframeBlock, isImageBlock, isLineBreakBlock, isLinkBlock, isListBlock, isMostReadBlock, isParagraphBlock, isPartnersBlock, isTextBlock, isVideoBlock } from "modules/editor/types";
import { GridProps } from "../Grid";
import { PartnersProps } from "modules/editor/blocks/Partners";
import { ContentSummaryFrontendBlock } from "modules/editor/blocks/ContentSummary";
import { CustomUrlBlockFrontendBlock } from "modules/editor/blocks/CustomUrlBlock";
import { MostReadProps } from "modules/editor/blocks/MostRead";
const Blockquote = dynamic<BlockquoteProps>(() => import("modules/editor/blocks/Blockquote").then(mod => mod.Blockquote));
const Body = dynamic<BodyProps>(() => import("modules/editor/blocks/Body").then(mod => mod.Body));
const CallToAction = dynamic<CallToActionProps>(() => import("modules/editor/blocks/CallToAction").then(mod => mod.CallToAction));
const ContentSummary = dynamic<ContentSummaryFrontendBlock>(() => import("modules/editor/blocks/ContentSummary").then(mod => mod.ContentSummary));
const Events = dynamic<EventsProps>(() => import("modules/editor/blocks/Events").then(mod => mod.Events));
const Grid = dynamic<GridProps>(() => import("modules/editor/blocks/Grid").then(mod => mod.Grid));
const Heading = dynamic<HeadingProps>(() => import("modules/editor/blocks/Heading").then(mod => mod.Heading));
const Iframe = dynamic<IframeProps>(() => import("modules/editor/blocks/Iframe").then(mod => mod.Iframe));
const Image = dynamic<ImageProps>(() => import("modules/editor/blocks/Image").then(mod => mod.Image));
const List = dynamic<ListProps>(() => import("modules/editor/blocks/List").then(mod => mod.List));
const Paragraph = dynamic<ParagraphProps>(() => import("modules/editor/blocks/Paragraph").then(mod => mod.Paragraph));
const Video = dynamic<VideoProps>(() => import("modules/editor/blocks/Video").then(mod => mod.Video));
const Partners = dynamic<PartnersProps>(() => import("modules/editor/blocks/Partners").then(mod => mod.Partners));
const MostRead = dynamic<MostReadProps>(() => import("modules/editor/blocks/MostRead").then(mod => mod.MostRead));
const CustomUrlBlock = dynamic<CustomUrlBlockFrontendBlock>(() => import("modules/editor/blocks/CustomUrlBlock").then(mod => mod.CustomUrlBlock));
interface BlocksRendererProps {
  blocks: FrontendBlock[];
}
interface BlockComponentProps {
  block: FrontendBlock;
}
export const BlockComponent = t0 => {
  "use memo";

  const $ = _c(52);
  const {
    block
  } = t0;
  if (isBlockquoteBlock(block)) {
    let t1;
    if ($[0] !== block.props || $[1] !== block.type) {
      t1 = <Blockquote {...block.props} data-testid={block.type} />;
      $[0] = block.props;
      $[1] = block.type;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  } else {
    if (isBodyBlock(block)) {
      let t1;
      if ($[3] !== block.props || $[4] !== block.type) {
        t1 = <Body {...block.props} data-testid={block.type} />;
        $[3] = block.props;
        $[4] = block.type;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      return t1;
    } else {
      if (isCallToActionBlock(block)) {
        let t1;
        if ($[6] !== block.props || $[7] !== block.type) {
          t1 = <CallToAction {...block.props} data-testid={block.type} />;
          $[6] = block.props;
          $[7] = block.type;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      } else {
        if (isEventsBlock(block)) {
          let t1;
          if ($[9] !== block) {
            t1 = <Events {...block} data-testid={block.type} />;
            $[9] = block;
            $[10] = t1;
          } else {
            t1 = $[10];
          }
          return t1;
        } else {
          if (isGridBlock(block)) {
            let t1;
            if ($[11] !== block) {
              t1 = <Grid {...block} data-testid={block.type} />;
              $[11] = block;
              $[12] = t1;
            } else {
              t1 = $[12];
            }
            return t1;
          } else {
            if (isContentSummaryBlock(block)) {
              let t1;
              if ($[13] !== block) {
                t1 = <ContentSummary {...block} data-testid={block.type} />;
                $[13] = block;
                $[14] = t1;
              } else {
                t1 = $[14];
              }
              return t1;
            } else {
              if (isHeadingBlock(block)) {
                let t1;
                if ($[15] !== block.props || $[16] !== block.type) {
                  t1 = <Heading {...block.props} data-testid={block.type} />;
                  $[15] = block.props;
                  $[16] = block.type;
                  $[17] = t1;
                } else {
                  t1 = $[17];
                }
                return t1;
              } else {
                if (isIframeBlock(block)) {
                  let t1;
                  if ($[18] !== block.props || $[19] !== block.type) {
                    t1 = <Iframe {...block.props} data-testid={block.type} />;
                    $[18] = block.props;
                    $[19] = block.type;
                    $[20] = t1;
                  } else {
                    t1 = $[20];
                  }
                  return t1;
                } else {
                  if (isImageBlock(block)) {
                    let alt;
                    let props;
                    if ($[21] !== block.props) {
                      ({
                        alt,
                        ...props
                      } = block.props);
                      $[21] = block.props;
                      $[22] = alt;
                      $[23] = props;
                    } else {
                      alt = $[22];
                      props = $[23];
                    }
                    let t1;
                    if ($[24] !== alt || $[25] !== block.type || $[26] !== props) {
                      t1 = <Image alt={alt} {...props} data-testid={block.type} />;
                      $[24] = alt;
                      $[25] = block.type;
                      $[26] = props;
                      $[27] = t1;
                    } else {
                      t1 = $[27];
                    }
                    return t1;
                  } else {
                    if (isLinkBlock(block)) {
                      let t1;
                      if ($[28] !== block.props || $[29] !== block.type) {
                        t1 = <Link {...block.props} data-testid={block.type} />;
                        $[28] = block.props;
                        $[29] = block.type;
                        $[30] = t1;
                      } else {
                        t1 = $[30];
                      }
                      return t1;
                    } else {
                      if (isListBlock(block)) {
                        let t1;
                        if ($[31] !== block.props || $[32] !== block.type) {
                          t1 = <List {...block.props} data-testid={block.type} />;
                          $[31] = block.props;
                          $[32] = block.type;
                          $[33] = t1;
                        } else {
                          t1 = $[33];
                        }
                        return t1;
                      } else {
                        if (isParagraphBlock(block)) {
                          let t1;
                          if ($[34] !== block.props || $[35] !== block.type) {
                            t1 = <Paragraph {...block.props} data-testid={block.type} />;
                            $[34] = block.props;
                            $[35] = block.type;
                            $[36] = t1;
                          } else {
                            t1 = $[36];
                          }
                          return t1;
                        } else {
                          if (isTextBlock(block)) {
                            let t1;
                            if ($[37] !== block.props || $[38] !== block.type) {
                              t1 = <Text {...block.props} data-testid={block.type} />;
                              $[37] = block.props;
                              $[38] = block.type;
                              $[39] = t1;
                            } else {
                              t1 = $[39];
                            }
                            return t1;
                          } else {
                            if (isVideoBlock(block)) {
                              let t1;
                              if ($[40] !== block.props || $[41] !== block.type) {
                                t1 = <Video {...block.props} data-testid={block.type} />;
                                $[40] = block.props;
                                $[41] = block.type;
                                $[42] = t1;
                              } else {
                                t1 = $[42];
                              }
                              return t1;
                            } else {
                              if (isPartnersBlock(block)) {
                                let t1;
                                if ($[43] !== block.partners || $[44] !== block.props || $[45] !== block.type) {
                                  t1 = <Partners partners={block.partners} {...block.props} data-testid={block.type} />;
                                  $[43] = block.partners;
                                  $[44] = block.props;
                                  $[45] = block.type;
                                  $[46] = t1;
                                } else {
                                  t1 = $[46];
                                }
                                return t1;
                              } else {
                                if (isMostReadBlock(block)) {
                                  let t1;
                                  if ($[47] !== block) {
                                    t1 = <MostRead {...block} data-testid={block.type} />;
                                    $[47] = block;
                                    $[48] = t1;
                                  } else {
                                    t1 = $[48];
                                  }
                                  return t1;
                                } else {
                                  if (isCustomUrlBlock(block)) {
                                    let t1;
                                    if ($[49] !== block) {
                                      t1 = <CustomUrlBlock {...block} data-testid={block.type} />;
                                      $[49] = block;
                                      $[50] = t1;
                                    } else {
                                      t1 = $[50];
                                    }
                                    return t1;
                                  } else {
                                    if (isLineBreakBlock(block)) {
                                      let t1;
                                      if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
                                        t1 = <br />;
                                        $[51] = t1;
                                      } else {
                                        t1 = $[51];
                                      }
                                      return t1;
                                    } else {
                                      return null;
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
};
export const BlocksRenderer = t0 => {
  "use memo";

  const $ = _c(4);
  const {
    blocks
  } = t0;
  let t1;
  if ($[0] !== blocks) {
    t1 = blocks.map(_temp);
    $[0] = blocks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <>{t1}</>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
function _temp(block, index) {
  return <BlockComponent block={block} key={index} />;
}